import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { RoomSelectorDesktopEl } from './RoomSelectorDesktop.style';
import { TrackingUtils, CMS, HotelRoomUtils } from '../../utils';
import { FieldType, RoomsProps } from '../../models';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { ConnectedRoomSelector } from './RoomSelector';
import { ConnectedRoomSelectorContent } from './RoomSelectorContent';
import { Div } from '../../layouts';
import { useRoomSelectorClassNames } from './useRoomSelectorClassNames';

const RoomSelectorDesktopInner = ({ value, fieldFocused, setFieldFocused, hasError, trackEvent, isHotel }) => {
    const node = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [roomSelectorClass] = useRoomSelectorClassNames(isOpen, hasError);
    useEffect(() => {
        if (fieldFocused === FieldType.ROOM ) {
            setOpen(true);
        }
    }, [fieldFocused]);
    const numPax = HotelRoomUtils.getTotalPax(value);

    const toggle = () => {
        if (fieldFocused !== FieldType.ROOM) {
            trackEvent({
                action: TrackingUtils.ACTION.DP,
                label: TrackingUtils.LABEL.changeRoomPax(),
            });
            setFieldFocused(FieldType.ROOM, true);
        } else {
            setFieldFocused(null);
        }

        setOpen(!isOpen);
    };

    const close = () => {
        setOpen(false);
        setFieldFocused(null);
    };

    return (
        <div ref={node}>
            <div className={roomSelectorClass.header} onClick={toggle}>
                <Div
                    className="odf-input odf-text-lg odf-input-m odf-input-inline odf-input-inline-last odf-select odf-space-outer-right-m"
                    data-testid="room-pax-selector-summary"
                    whiteSpace="nowrap"
                    style={{ paddingTop: isHotel ? 10 : 9 }}
                >
                    {/*
                        Temporarily use of 'dynpack.searchSummary.rooms' key instead of 'roomSelector.rooms'
                        see OFIS-1588 && CONTENT-10593
                    */}
                    <CMS id="dynpack.searchSummary.rooms" values={{ count: value.length }} />
                    <div className="odf-icon odf-icon-pax" data-testid="room-selector-pax-number" />{ numPax }
                </Div>
                <span className={roomSelectorClass.arrow} />
            </div>
            <Dropdown open={isOpen} onClose={close} parentRef={node}>
                <RoomSelectorDesktopEl
                    className={roomSelectorClass.paxSelector}
                    data-testid="room-pax-selector-picker"
                >
                    <ConnectedRoomSelectorContent
                        hasError={hasError}
                        onClose={close}
                    />
                </RoomSelectorDesktopEl>
            </Dropdown>
            { hasError && (
                <span className="odf-label odf-text-sm odf-text-negative odf-space-outer-xs">
                    <CMS id="roomSelector.children.error" />
                </span>
            )}
        </div>
    );
};

RoomSelectorDesktopInner.propTypes = {
    value: RoomsProps.isRequired,
    fieldFocused: PropTypes.string,
    setFieldFocused: PropTypes.func.isRequired,
    hasError: PropTypes.bool.isRequired,
    trackEvent: PropTypes.func.isRequired,
    isHotel: PropTypes.bool,
};

RoomSelectorDesktopInner.defaultProps = {
    fieldFocused: null,
    isHotel: PropTypes.bool,
};

export const RoomSelector = ({ fieldFocused, setFieldFocused }) => (
    <ConnectedRoomSelector render={({ dynamicValue, hasError, trackEvent, isHotel }) => (
        <RoomSelectorDesktopInner
            value={dynamicValue}
            fieldFocused={fieldFocused}
            setFieldFocused={setFieldFocused}
            hasError={hasError}
            trackEvent={trackEvent}
            isHotel={isHotel}
        />
    )}
    />
);
RoomSelector.propTypes = {
    fieldFocused: PropTypes.string,
    setFieldFocused: PropTypes.func.isRequired,
};

RoomSelector.defaultProps = {
    fieldFocused: null,
};
